import React, { useState } from 'react';
import { createSucursal } from '../../services/pos/sucursalService';
import { X } from 'lucide-react';

interface AddSucursalPageProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AddSucursalPage: React.FC<AddSucursalPageProps> = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        direccion: '',
        codigo: '',
        telefono: '',
        email: '',
        activo: true
    });

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            if (!formData.nombre.trim()) throw new Error('El nombre es requerido');
            if (!formData.direccion.trim()) throw new Error('La dirección es requerida');
            if (!formData.codigo.trim()) throw new Error('El código es requerido');

            if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                throw new Error('El email no es válido');
            }

            const sucursalData = {
                nombre: formData.nombre.trim(),
                direccion: formData.direccion.trim(),
                codigo: formData.codigo.trim().toUpperCase(),
                telefono: formData.telefono.trim() || null,
                email: formData.email.trim() || null,
                activo: formData.activo
            };

            await createSucursal(sucursalData);
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Error al crear la sucursal');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            
            {/* Aumentamos el max-w a 2xl para las dos columnas */}
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-2xl relative overflow-hidden">
                
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Nueva Sucursal</h3>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-6">
                        {/* Grid de 2 Columnas */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            
                            {/* Columna Izquierda: Identificación */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Identificación</h4>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Ej: Sucursal Norte"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                                    <input
                                        type="text"
                                        name="codigo"
                                        value={formData.codigo}
                                        onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                                        required
                                        maxLength={10}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="SUC-001"
                                    />
                                </div>
                            </div>

                            {/* Columna Derecha: Contacto */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contacto</h4>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                                    <input
                                        type="tel"
                                        name="telefono"
                                        value={formData.telefono}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="7000-0000"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="sucursal@empresa.com"
                                    />
                                </div>
                            </div>

                            {/* Fila Completa: Dirección */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección Completa</label>
                                <input
                                    type="text"
                                    name="direccion"
                                    value={formData.direccion}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Calle, Número, Ciudad..."
                                />
                            </div>

                            {/* Fila Completa: Estado */}
                            <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg flex items-center justify-between border border-gray-100">
                                <div>
                                    <span className="block text-sm font-medium text-gray-800">Estado Operativo</span>
                                    <p className="text-xs text-gray-500">{formData.activo ? 'La sucursal aparecerá en los listados' : 'La sucursal quedará deshabilitada'}</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={formData.activo}
                                        onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked }))}
                                    />
                                    <div className={`w-11 h-6 rounded-full transition-colors ${formData.activo ? 'bg-[#c1348b]' : 'bg-gray-300'}`}>
                                        <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${formData.activo ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Footer con Botones */}
                    <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-5 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 font-bold flex items-center gap-2"
                        >
                            {submitting && (
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            )}
                            {submitting ? 'Guardando...' : 'Crear Sucursal'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddSucursalPage;